import React, { useEffect, useState } from "react";
import { IoIosClose } from "react-icons/io";
import { toast } from "react-toastify";
import Dash from "../../components/Dash";
import EditSalesPurchase from "../../components/dashboard/EditSalesPurchase";
import ProfitAndPurchase from "../../components/dashboard/ProfitAndPurchase";
import TotalCustomers from "../../components/dashboard/TotalCustomers";
import Layout from "../../components/Layout/Layout";
import Loading from "../../components/Loading";
import CustomModal, { ModalInput } from "../../components/Modal";
import DeleteModal from "../../components/modals/DeleteModal";
import CustomSelect from "../../components/Select";
import Table from "../../components/Table";
import {
  admindashboardPurchaseOrders,
  completeOrder,
  dashboardCompletedOrders,
  deleteOrder,
  deletePurchaseOrder,
  deleteSalary,
  getAdminDashboard,
  getAllCustomers,
  getCustomerDashData,
  getCustomerGroup,
  getDashboardCount,
  getDashboardProfit,
  getDashboardPurchase,
  getDashboardSalary,
  getDashboardSale,
  getMarketDashboardHandler,
  getProductData,
  updateOrder,
} from "../../utils/api";
import { formatDate } from "../../utils/formatData";
import useStore from "../../utils/store";

const Dashboard = () => {
  const [dash, setDash] = useState([]);
  const [pendingOrders, setPendingOrders] = useState([]);
  const [open, setOpen] = useState(false);
  const startDate = useStore((state) => state.startDate);
  const endDate = useStore((state) => state.endDate);
  const [dashboardCount, setDashCount] = useState(null);
  const [totalCustomers, setTotalCustomers] = useState(null);

  const [customerData, setCustomerData] = useState([]);
  const [customers, setCustomers] = React.useState([]);
  const [dashboardData, setDashboardData] = React.useState([]);
  const getCustomers = async () => {
    setLoading(true);
    const [success, data] = await getAllCustomers(startDate, endDate);
    if (success) {
      setCustomers(data);
    }
    setLoading(false);
  };

  const getDashboardCountHandler = async (type) => {
    setLoading(true);
    const [success, data] = await getDashboardCount(type, startDate, endDate);
    if (success && data) {
      setDashCount({
        dataType: type,
        ...data,
      });
      setDashboardData(data.data || []);
      setOpen(false);
      setTotalCustomers(null);
    } else {
      toast.error(`Failed to load ${type} data. Please check date range.`);
    }
    setLoading(false);
  };

  const getCustomerGroupHandler = async () => {
    setLoading(true);
    const [success, data] = await getCustomerGroup(startDate, endDate);
    if (success && data) {
      setTotalCustomers(data);
      setOpen(false);
      setDashboardData([]);
      setDashCount(null);
    } else {
      toast.error("Failed to load customer groups.");
    }
    setLoading(false);
  };
  const [newOrder, setNewOrder] = React.useState({});
  const [update, setUpdate] = React.useState(false);
  const [products, setProducts] = React.useState([]);
  const [loading, setLoading] = useState(false);
  const [completedOrders, setCompletedOrders] = useState([]);
  const [purchaseOrders, setPurchaseOrders] = useState([]);

  const user = useStore((state) => state.user);

  const getCustomerDataById = async (id) => {
    setLoading(true);
    const [success, data] = await getCustomerDashData(id, startDate, endDate);
    if (success) {
      setCustomerData(data.map((item) => ({ ...item, id: id, _id: item.id })));
    }
    setLoading(false);
  };

  const getAllOrdersHandler = async () => {
    setLoading(true);
    const [success, data] = await getMarketDashboardHandler(startDate, endDate);
    if (success) {
      setPendingOrders(data ?? []);
    }
    setLoading(false);
  };

  const getAllPurchaseOrdersHandler = async () => {
    setLoading(true);
    const [success, data] = await admindashboardPurchaseOrders(
      startDate,
      endDate
    );
    if (success) {
      setPurchaseOrders(data || []);
    }
    setLoading(false);
  };

  const getAllCompletedOrdersHandler = async () => {
    setLoading(true);
    const [success, data] = await dashboardCompletedOrders(startDate, endDate);
    if (success) {
      setCompletedOrders(data || []);
    }
    setLoading(false);
  };

  const loadInitialDashboard = async () => {
    setLoading(true);
    // 1. Fetch primary KPI cards and filters first (renders within ~200ms)
    await Promise.allSettled([
      getAdminDashboard(startDate, endDate).then(([success, data]) => {
        if (success) setDash(data);
      }),
      getMarketDashboardHandler(startDate, endDate).then(([success, data]) => {
        if (success) setPendingOrders(data ?? []);
      }),
      getProductData(startDate, endDate).then(([success, data]) => {
        if (success) setProducts(data);
      }),
      getAllCustomers(startDate, endDate).then(([success, data]) => {
        if (success) setCustomers(data);
      }),
    ]);
    setLoading(false);

    // 2. Heavy order tables load asynchronously in the background without freezing the UI
    Promise.allSettled([
      admindashboardPurchaseOrders(startDate, endDate).then(([success, data]) => {
        if (success) setPurchaseOrders(data || []);
      }),
      dashboardCompletedOrders(startDate, endDate).then(([success, data]) => {
        if (success) setCompletedOrders(data || []);
      }),
    ]);
  };

  useEffect(() => {
    loadInitialDashboard();
  }, [startDate, endDate]);

  return (
    <Layout reload={loadInitialDashboard}>
      {loading && <Loading />}
      <div className="flex gap-3 flex-col md:flex-row flex-wrap">
        <button
          onClick={() => {
            setDashboardData([]);
            setDashCount(null);
            setOpen(!open);
            setTotalCustomers(null);
          }}
          className="flex-1"
        >
          <Dash
            title={"Pending Orders"}
            value={open && dash?.totalPendingOrders}
          />
        </button>
        <button onClick={getCustomerGroupHandler} className="flex-1">
          <Dash
            title={" Customers"}
            value={totalCustomers && dash?.totalCustomers}
          />
        </button>
        {user?.type !== "ViewAdmin" && (
          <>
            <button
              onClick={() => getDashboardCountHandler("Profit")}
              className="flex-1"
            >
              <Dash
                title={" Profit"}
                value={
                  dashboardCount?.dataType === "Profit" &&
                  dash?.totalProfit?.toFixed(2)
                }
              />
            </button>
            <button
              onClick={() => getDashboardCountHandler("Purchase")}
              className="flex-1"
            >
              <Dash
                title={" Purchase"}
                value={
                  dashboardCount?.dataType === "Purchase" &&
                  dash?.totalPurchase?.toFixed(2)
                }
              />
            </button>
            <button
              onClick={() => getDashboardCountHandler("Sale")}
              className="flex-1"
            >
              <Dash
                title={" Sales"}
                value={
                  dashboardCount?.dataType === "Sale" &&
                  dash?.totalSales?.toFixed(2)
                }
              />
            </button>
          </>
        )}
      </div>
      <TotalCustomers
        customerData={customerData}
        totalCustomers={totalCustomers}
        getCustomerDataById={getCustomerDataById}
      />
      {dashboardCount && (
        <div className="flex gap-4 justify-between items-start flex-wrap xl:flex-nowrap mt-4 w-full">
          {/* Left Column: Category Menu */}
          <div className="flex flex-col gap-3 items-start min-w-[200px]">
            <h1 className="font-medium bg-secondary border border-black rounded px-4 py-1">
              {dashboardCount.dataType}
            </h1>
            {["Tag", "Stancil", "Trading", "Other", ...(dashboardCount.dataType === "Purchase" ? ["salary"] : [])].map((item, index) => {
              const amount = Number(dashboardCount[item] || 0);

              return (
                <div key={index} className="flex items-center gap-2 flex-wrap">
                  <button
                    onClick={async () => {
                      setLoading(true);
                      if (dashboardCount.dataType === "Purchase") {
                        if (item === "salary") {
                          const [success, data] = await getDashboardSalary(
                            startDate,
                            endDate
                          );
                          if (success) {
                            setDashboardData(data);
                          }
                        } else {
                          const [success, data] = await getDashboardPurchase(
                            item,
                            startDate,
                            endDate
                          );
                          if (success) {
                            setDashboardData(data);
                          }
                        }
                      } else if (dashboardCount.dataType === "Profit") {
                        const [success, data] = await getDashboardProfit(
                          item,
                          startDate,
                          endDate
                        );
                        if (success) {
                          setDashboardData(data);
                        }
                      } else {
                        const [success, data] = await getDashboardSale(
                          item,
                          startDate,
                          endDate
                        );
                        if (success) {
                          setDashboardData(data);
                        }
                      }
                      setLoading(false);
                    }}
                    className="hover:bg-primary px-2 py-1 rounded-md cursor-pointer text-left transition"
                  >
                    <h1 className="font-medium">
                      {item}( Rs {amount.toFixed(2)})
                    </h1>
                  </button>
                </div>
              );
            })}
          </div>
          {dashboardData?.length > 0 &&
            (dashboardData?.[0]?.type === "Salary" ? (
              <div className="flex flex-col gap-3 items-start pl-6">
                <h1 className="font-medium bg-secondary border border-black rounded px-4 py-1  ">
                  Salary Data
                </h1>
                <Table
                  data={[
                    ...dashboardData,
                    {
                      staff: {
                        userName: "Total",
                      },
                      amount: dashboardData.reduce(
                        (acc, item) => acc + item.amount,
                        0
                      ),
                    },
                  ]}
                  columns={[
                    {
                      title: "Name",
                      render: (record) => (
                        <p
                          className={
                            record?.staff?.userName === "Total"
                              ? "font-bold text-blue-700"
                              : ""
                          }
                        >
                          {record?.staff?.userName}
                        </p>
                      ),
                    },
                    {
                      title: "Amount",
                      render: (record) => record?.amount,
                    },
                  ]}
                />
              </div>
            ) : (
              <div className="flex flex-col gap-3 items-start pl-6">
                <h1 className="font-medium bg-secondary border border-black rounded px-4 py-1  ">
                  {dashboardCount.dataType} Data
                </h1>
                <Table
                  data={dashboardData?.filter(
                    (item) => dashboardCount.dataType === item?.productType
                  )}
                  columns={[
                    {
                      title: "Product Name",
                      render: (record) => record?.productName,
                    },
                    {
                      title:
                        dashboardCount.dataType === "Profit"
                          ? "Price"
                          : "Quantity",
                      render: (record) => record?.total,
                    },
                  ]}
                />
              </div>
            ))}
        </div>
      )}
      {open && (
        <Table
          data={pendingOrders}
          columns={[
            {
              title: "Date",
              dataIndex: "createdAt",
              render: (record) => formatDate(record?.updatedAt),
            },
            {
              title: "Customer Name",
              render: (record) => record?.customer?.userName,
            },

            {
              title: "description",
              dataIndex: "description",
            },
            {
              title: "Location",
              render: (record) => record?.customer?.group?.name,
            },
            {
              title: "Product Name",
              render: (record) => record?.product?.productName,
            },
            {
              title: "FSSAI",
              render: (record) => record?.customer?.fssai,
            },
            {
              title: "Agency",
              render: (record) => record?.customer?.agency,
            },
            {
              title: "Qnt",
              dataIndex: "quantity",
            },
            {
              title: "Rate",
              dataIndex: "rate",
            },
            {
              title: "Gst",
              dataIndex: "gst",
            },
            {
              title: "Total",
              dataIndex: "totalAmount",
              render: (record) => record.totalAmount.toFixed(2),
            },
            {
              title: "User",
              render: (row) => (
                <div>
                  {row?.createdByName},{row?.completedByName}
                </div>
              ),
            },
            {
              title: "Image",
              render: (row) => (
                <a href={row?.customer?.image} target="_blank" rel="noreferrer">
                  {row?.customer?.image ? "View" : ""}
                </a>
              ),
            },
            {
              title: user?.type === "ViewAdmin" ? "" : "Actions",
              render: (record) =>
                user?.type === "ViewAdmin" ? null : (
                  <div className="flex items-center justify-center gap-3">
                    <button
                      className="bg-red-600  px-2 py-1 "
                      onClick={() => {
                        setUpdate(true);
                        setNewOrder({
                          _id: record._id,
                          customerId: record.customer._id,
                          productId: record.product._id,
                          description: record.description,
                          quantity: record.quantity,
                          rate: record.rate,
                          gst: record.gst,

                          date: record?.date,
                        });
                      }}
                    >
                      Edit
                    </button>
                    <button
                      className="bg-green-500  px-2 py-1 "
                      onClick={async () => {
                        setLoading(true);
                        const [success, data] = await completeOrder(record._id, {
                          type: "admin",
                          completedById: user?.admin?._id || user?.staff?._id,
                        });
                        if (success) {
                          toast.success("Order Completed");
                          getAllOrdersHandler();
                        } else {
                          toast.error(data);
                        }
                        setLoading(false);
                      }}
                    >
                      Complete
                    </button>
                    <button
                      className="bg-red-600  px-2 py-1 text-2xl"
                      onClick={async () => {
                        setLoading(true);
                        const [success, data] = await deleteOrder(record._id);
                        if (success) {
                          toast.success("Order Deleted");
                          getAllOrdersHandler();
                        } else {
                          toast.error(data);
                        }
                        setLoading(false);
                      }}
                    >
                      <IoIosClose />
                    </button>
                  </div>
                ),
            },
          ]}
        />
      )}
      <CustomModal
        open={update}
        setOpen={setUpdate}
        loading={loading}
        title={"Update Order"}
        onClick={async () => {
          console.log(newOrder);

          setLoading(true);
          const [success, data] = await updateOrder(newOrder._id, {
            customerId: newOrder.customerId,
            productId: newOrder.productId,
            description: newOrder.description,
            quantity: newOrder.quantity,
            rate: newOrder.rate,
            gst: newOrder.gst,
            date: newOrder.date,
          });
          if (success) {
            toast.success("Order Updated");
            setUpdate(false);
            getAllOrdersHandler();
          } else {
            toast.error(data);
          }
          setLoading(false);
        }}
      >
        <div className="flex    gap-3 flex-col ">
          <div className="flex justify-between flex-col md:flex-row  gap-2">
            <div className="">Customer</div>
            <CustomSelect
              width={"256px"}
              onChange={(e) => {
                setNewOrder({ ...newOrder, customerId: e.target.value });
              }}
              options={customers.map((customer) => ({
                value: customer._id,
                label: customer.userName,
              }))}
              value={newOrder.customerId}
            />
          </div>
          <ModalInput
            title={"description"}
            value={newOrder?.description}
            onChange={(e) => {
              setNewOrder({ ...newOrder, description: e.target.value });
            }}
          />

          <div className="flex justify-between flex-col md:flex-row   gap-2">
            <div className="">Items</div>
            <CustomSelect
              width={"256px"}
              onChange={(e) => {
                setNewOrder({ ...newOrder, productId: e.target.value });
              }}
              options={products.map((item) => ({
                value: item._id,
                label: item.productName,
              }))}
              value={newOrder?.productId}
            />
          </div>

          <ModalInput
            type={"number"}
            title={"Quantity"}
            onChange={(e) => {
              setNewOrder({ ...newOrder, quantity: e.target.value });
            }}
            value={newOrder?.quantity}
          />

          <ModalInput
            title={"GST"}
            type={"number"}
            onChange={(e) => {
              setNewOrder({ ...newOrder, gst: e.target.value });
            }}
            value={newOrder?.gst}
          />
          <ModalInput
            title={"Rate"}
            type={"number"}
            onChange={(e) => {
              setNewOrder({ ...newOrder, rate: e.target.value });
            }}
            value={newOrder?.rate}
          />
          <ModalInput
            title={"Date"}
            type={"date"}
            onChange={(e) => {
              setNewOrder({ ...newOrder, date: e.target.value });
            }}
          />
        </div>
      </CustomModal>
      <ProfitAndPurchase
        dashboardCount={dashboardCount}
        dash={dash}
        customers={customers}
      />

      {dashboardCount?.dataType === "Purchase" && (
        <Table
          data={[...purchaseOrders]?.reverse()}
          columns={[
            {
              title: "Date",
              dataIndex: "date",
              render: (record) => (
                <div
                  className={`${
                    record?.paymentType === "Salary"
                      ? "bg-yellow-400 px-3  border border-black"
                      : ""
                  }`}
                >
                  {formatDate(record?.date)}
                </div>
              ),
            },
            {
              title: "Customer Name",
              render: (record) => (
                <div
                  className={`${
                    record?.paymentType === "Salary"
                      ? "bg-yellow-400 px-3  border border-black"
                      : ""
                  }`}
                >
                  {record?.paymentType === "Salary"
                    ? record?.staff?.userName
                    : record?.customer?.userName}
                </div>
              ),
            },
            {
              title: "description",
              dataIndex: "description",
            },
            {
              title: "Location",
              render: (record) => record?.customer?.group?.name,
            },
            {
              title: "Product Name",
              render: (record) => record?.product?.productName,
            },
            {
              title: "Qnt",
              dataIndex: "quantity",
            },
            {
              title: "Rate",
              dataIndex: "rate",
            },
            {
              title: "Gst",
              dataIndex: "gst",
            },
            {
              title: "Total",
              dataIndex: "totalAmount",
              render: (record) => (
                <div
                  className={`${
                    record?.paymentType === "Salary"
                      ? "bg-yellow-400 px-3  border border-black"
                      : ""
                  }`}
                >
                  {record?.paymentType === "Salary"
                    ? record?.amount?.toFixed(2)
                    : record.totalAmount?.toFixed(2)}
                </div>
              ),
            },
            {
              title: "Completed By",
              dataIndex: "CompletedAmount",
              render: (record) => (
                <div
                  className={`${
                    record?.paymentType === "Salary"
                      ? "bg-yellow-400 px-3  border border-black"
                      : ""
                  }`}
                >
                  {record?.completedByName}
                </div>
              ),
            },
            {
              title: "User",
              render: (row) => row.createdByName,
            },
            {
              title: "Image",
              render: (row) => (
                <a href={row?.customer?.image} target="_blank" rel="noreferrer">
                  {row?.customer?.image ? "View" : ""}
                </a>
              ),
            },
            {
              title: "Actions",
              render: (record) => (
                <div className="flex items-center gap-2">
                  <EditSalesPurchase
                    data={record}
                    reload={getAllPurchaseOrdersHandler}
                    customers={customers}
                    products={products || []}
                  />
                  <DeleteModal
                    loading={loading}
                    onClick={async () => {
                      setLoading(true);
                      if (record?.paymentType === "Salary") {
                        const [success, data] = await deleteSalary(record._id);
                        if (success) {
                          toast.success("Salary Deleted");
                          getAllPurchaseOrdersHandler();
                        } else {
                          toast.error(data || "Salary Not Deleted");
                        }
                      } else {
                        const [success, data] = await deletePurchaseOrder(
                          record._id
                        );
                        if (success) {
                          toast.success("Purchase Order Deleted");
                          getAllPurchaseOrdersHandler();
                        } else {
                          toast.error(data || "Purchase Order Not Deleted");
                        }
                      }
                      setLoading(false);
                    }}
                  >
                    Delete
                  </DeleteModal>
                </div>
              ),
            },
            {
              title: "PDF",
              link: "/admin/pdf/purchase",
            },
          ]}
        />
      )}
      {dashboardCount?.dataType === "Sale" && (
        <Table
          data={[...completedOrders]?.reverse()}
          columns={[
            {
              title: "Date",
              dataIndex: "updatedAt",
              render: (record) =>
                record?.completedDate
                  ? formatDate(record?.completedDate)
                  : formatDate(record?.updatedAt),
            },
            {
              title: "Customer Name",

              render: (record) => (
                <p>
                  {record?.customer?.userName}{" "}
                  {record?.clients?.length > 0 && (
                    <>
                      (
                      {record?.clients?.map((item) => item.userName)?.join(",")}
                      )
                    </>
                  )}
                </p>
              ),
            },
            {
              title: "description",
              dataIndex: "description",
              render: (record) =>
                `${record?.description} ${
                  record?.dispatchNote ? `, ${record?.dispatchNote}` : ""
                }`,
            },
            {
              title: "Location",
              render: (record) => record?.customer?.group?.name,
            },
            {
              title: "Product Name",
              render: (record) => record?.product?.productName,
            },
            {
              title: "Qnt",
              dataIndex: "quantity",
            },
            {
              title: "Rate",
              dataIndex: "rate",
            },
            {
              title: "Gst",
              dataIndex: "gst",
            },
            {
              title: "Total",
              dataIndex: "totalAmount",
              render: (record) => record?.totalAmount?.toFixed(2),
            },
            {
              title: "Actions",
              render: (record) => (
                <EditSalesPurchase
                  data={record}
                  reload={getAllCompletedOrdersHandler}
                  customers={customers}
                  products={products || []}
                  type={"sales"}
                />
              ),
            },
            {
              title: "PDF",
              link: "/admin/pdf/sales",
            },
          ]}
        />
      )}
    </Layout>
  );
};

export default Dashboard;
